import axios from 'axios';
import React, { useEffect, useState, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import ReactPaginate from 'react-paginate';
import AdminLayout from '../../components/AdminLayout';
import { API_URL } from '../../config';

function Articles() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [loadingArticle, setLoadingArticle] = useState(false);
  const [filter, setFilter] = useState('all'); // all, pending, approved
  const [searchInput, setSearchInput] = useState(''); // Input field value
  const [searchQuery, setSearchQuery] = useState(''); // Actual search query used in API
  const [currentPage, setCurrentPage] = useState(0);
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 10,
    pages: 0
  });

  const fetchArticles = useCallback(async (page) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('adminToken');

      // react-paginate uses 0-based indexing, but backend uses 1-based
      const pageNumber = page + 1;
      const limit = 10;

      let url = `${API_URL}/articles?page=${pageNumber}&limit=${limit}&sort=-createdAt`;
      if (filter === 'pending') {
        url += '&approved=false';
      } else if (filter === 'approved') {
        url += '&approved=true';
      }

      // Add search query parameter if search query exists
      if (searchQuery.trim()) {
        url += `&search=${encodeURIComponent(searchQuery.trim())}`;
      }

      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setArticles(response.data.data || []);
      
      // Store pagination info from response
      if (response.data.pagination) {
        setPagination(response.data.pagination);
      }
      
      setLoading(false);
      setError(null);
    } catch (err) {
      console.error('Fetch articles error:', err);
      setError('Failed to load articles');
      setLoading(false);
    }
  }, [filter, searchQuery]);

  useEffect(() => {
    // Reset to first page when filter or search query changes
    setCurrentPage(0);
  }, [filter, searchQuery]);

  useEffect(() => {
    fetchArticles(currentPage);
  }, [currentPage, fetchArticles]);

  const handleSearch = () => {
    setSearchQuery(searchInput.trim());
    setCurrentPage(0);
  };

  const handleClearSearch = () => {
    setSearchInput('');
    setSearchQuery('');
    setCurrentPage(0);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleApprove = async (id, approved) => {
    try {
      const token = localStorage.getItem('adminToken');

      await axios.patch(
        `${API_URL}/articles/approve/${id}`,
        { approved },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Update articles list
      setArticles(articles.map(article => {
        if (article._id === id) {
          return { ...article, approved };
        }
        return article;
      }));

      // Close modal if open
      if (showModal && selectedArticle && selectedArticle._id === id) {
        setSelectedArticle({ ...selectedArticle, approved });
      }

      // Refresh articles to update pagination if needed
      fetchArticles(currentPage);
    } catch (err) {
      console.error('Approve article error:', err);
      alert('Failed to update article approval status');
    }
  };

  const handlePageClick = (event) => {
    const selectedPage = event.selected;
    setCurrentPage(selectedPage);
  };

  const viewArticle = async (article) => {
    try {
      setLoadingArticle(true);
      setShowModal(true);
      const token = localStorage.getItem('adminToken');
      
      const response = await axios.get(
        `${API_URL}/articles/${article._id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success && response.data.data) {
        setSelectedArticle(response.data.data);
      } else {
        // Fallback to the article from list if API call fails
        setSelectedArticle(article);
        alert('Failed to load full article details');
      }
    } catch (err) {
      console.error('Fetch article error:', err);
      // Fallback to the article from list if API call fails
      setSelectedArticle(article);
      alert('Failed to load full article details');
    } finally {
      setLoadingArticle(false);
    }
  };

  const closeModal = () => {
    setShowModal(false);
  };

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="text-center py-10">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
          <p className="mt-2 text-gray-600">Loading articles...</p>
        </div>
      </AdminLayout>
    );
  }

  if (error) {
    return (
      <AdminLayout>
        <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-4">
          <div className="flex">
            <div>
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="mb-6">
        <div className="flex justify-between items-center mb-4">
          <h1 className="text-2xl font-semibold text-gray-900">Articles Management</h1>

          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-700">Filter:</span>
            <select
              className="rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
            >
              <option value="all">All Articles</option>
              <option value="pending">Pending Approval</option>
              <option value="approved">Approved</option>
            </select>
          </div>
        </div>

        {/* Search Filter for Subject + Author */}
        <div className="flex items-center space-x-2">
          <label htmlFor="search" className="text-sm font-medium text-gray-700 whitespace-nowrap">
            Search (Subject + Author):
          </label>
          <div className="flex-1 max-w-md flex items-center space-x-2">
            <input
              id="search"
              type="text"
              placeholder="Search by title or author..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyPress={handleKeyPress}
              className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm px-3 py-2"
            />
            <button
              onClick={handleSearch}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Search
            </button>
          </div>
          {(searchInput || searchQuery) && (
            <button
              onClick={handleClearSearch}
              className="px-3 py-2 text-sm text-gray-600 hover:text-gray-800 border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Clear
            </button>
          )}
        </div>
      </div>

      {articles.length === 0 ? (
        <div className="bg-white shadow overflow-hidden sm:rounded-md p-6 text-center">
          <p className="text-gray-500">No articles found</p>
        </div>
      ) : (
        <div className="bg-white shadow overflow-hidden sm:rounded-md">
          <ul className="divide-y divide-gray-200">
            {articles.map((article) => (
              <li key={article._id}>
                <div className="px-4 py-4 sm:px-6">
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-blue-600 truncate cursor-pointer hover:underline" onClick={() => viewArticle(article)}>
                        {article.title}
                      </p>
                      <p className="mt-2 flex items-center text-sm text-gray-500">
                        <span>By {article.author}</span>
                        <span className="mx-1">•</span>
                        <span>{formatDate(article.createdAt)}</span>
                      </p>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${article.approved ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                        {article.approved ? 'Approved' : 'Pending'}
                      </span>
                      {article.approved ? (
                        <button
                          onClick={() => handleApprove(article._id, false)}
                          className="bg-red-100 text-red-800 px-3 py-1 rounded-md text-xs font-medium"
                        >
                          Reject
                        </button>
                      ) : (
                        <button
                          onClick={() => handleApprove(article._id, true)}
                          className="bg-green-100 text-green-800 px-3 py-1 rounded-md text-xs font-medium"
                        >
                          Approve
                        </button>
                      )}
                    </div>
                  </div>
                  <p className="mt-1 text-sm text-gray-500 line-clamp-2">{article.description}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className="mt-6 flex justify-center">
          <ReactPaginate
            breakLabel="..."
            nextLabel="Next >"
            onPageChange={handlePageClick}
            pageRangeDisplayed={5}
            pageCount={pagination.pages}
            previousLabel="< Previous"
            renderOnZeroPageCount={null}
            containerClassName="flex items-center space-x-2"
            pageClassName="px-3 py-2 border border-gray-300 rounded-md hover:bg-gray-50 cursor-pointer"
            pageLinkClassName="text-gray-700"
            previousClassName="px-3 py-2 border border-gray-300 rounded-md hover:bg-gray-50 cursor-pointer"
            previousLinkClassName="text-gray-700"
            nextClassName="px-3 py-2 border border-gray-300 rounded-md hover:bg-gray-50 cursor-pointer"
            nextLinkClassName="text-gray-700"
            breakClassName="px-3 py-2 text-gray-500"
            activeClassName="bg-blue-500 text-white border-blue-500"
            activeLinkClassName="text-white"
            disabledClassName="opacity-50 cursor-not-allowed"
            disabledLinkClassName="cursor-not-allowed"
            forcePage={currentPage}
          />
        </div>
      )}

      {/* Article View Modal */}
      {showModal && (
        <div className="fixed inset-0 overflow-y-auto z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
              <h3 className="text-lg font-medium text-gray-900">Article Preview</h3>
              <button onClick={closeModal} className="text-gray-400 hover:text-gray-500">
                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            {loadingArticle ? (
              <div className="p-6 text-center">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
                <p className="mt-2 text-gray-600">Loading article...</p>
              </div>
            ) : selectedArticle ? (
              <>
                <div className="p-6 overflow-y-auto" style={{ maxHeight: 'calc(90vh - 10rem)' }}>
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h2 className="text-xl font-bold text-gray-900">{selectedArticle.title}</h2>
                      <p className="text-sm text-gray-500">By {selectedArticle.author} • {formatDate(selectedArticle.createdAt)}</p>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${selectedArticle.approved ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                        {selectedArticle.approved ? 'Approved' : 'Pending'}
                      </span>
                    </div>
                  </div>

                  <div className="mb-4">
                    <p className="font-medium text-gray-700">Description:</p>
                    <p className="text-gray-600">{selectedArticle.description}</p>
                  </div>

                  {selectedArticle.image && (
                    <div className="mb-4">
                      <img
                        src={selectedArticle.image}
                        alt={selectedArticle.title}
                        className="w-full h-64 object-cover rounded-lg"
                        onError={(e) => e.target.style.display = 'none'}
                      />
                    </div>
                  )}

                  <div className="prose max-w-none">
                    {selectedArticle.contentType === 'richtext' ? (
                      <div dangerouslySetInnerHTML={{ __html: selectedArticle.content }} />
                    ) : (
                      // Check if content contains HTML tags even when contentType is markdown
                      /<[^>]*>/.test(selectedArticle.content || '') ? (
                        <div dangerouslySetInnerHTML={{ __html: selectedArticle.content || '' }} />
                      ) : (
                        <ReactMarkdown>{selectedArticle.content}</ReactMarkdown>
                      )
                    )}
                  </div>
                </div>
                <div className="px-6 py-4 bg-gray-50 text-right">
                  {selectedArticle.approved ? (
                    <button
                      onClick={() => handleApprove(selectedArticle._id, false)}
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                    >
                      Reject Article
                    </button>
                  ) : (
                    <button
                      onClick={() => handleApprove(selectedArticle._id, true)}
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                    >
                      Approve Article
                    </button>
                  )}
                </div>
              </>
            ) : null}
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

export default Articles; 